import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDatepicker, MatDatepickerIntl } from '@angular/material/datepicker';
import { TranslateService } from '@ngx-translate/core';
import {
  add as dateAdd,
  endOfDay,
  isAfter,
  isBefore,
  startOfYear,
} from 'date-fns';
import { DateRangeMode } from '../../services/DateRangeMode';
import { DateService } from 'src/app/modules/shared/services/date.service';

@Component({
  selector: 'vi-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  providers: [
    {
      provide: MatDatepickerIntl,
      useFactory: (translate: TranslateService) => {
        const intl = new MatDatepickerIntl();
        const translations = [
          'ACCESSIBILITY.ENERGY_COCKPIT.ENERGY_CHART.OPEN_CALENDAR',
          'ACCESSIBILITY.ENERGY_COCKPIT.ENERGY_CHART.CHOOSE_DATE',
          'ACCESSIBILITY.ENERGY_COCKPIT.ENERGY_CHART.PREVIOUS_YEAR',
          'ACCESSIBILITY.ENERGY_COCKPIT.ENERGY_CHART.NEXT_YEAR',
          'ACCESSIBILITY.ENERGY_COCKPIT.ENERGY_CHART.PREVIOUS_MONTH',
          'ACCESSIBILITY.ENERGY_COCKPIT.ENERGY_CHART.NEXT_MONTH',
          'ACCESSIBILITY.ENERGY_COCKPIT.ENERGY_CHART.PREVIOUS_24_YEARS',
          'ACCESSIBILITY.ENERGY_COCKPIT.ENERGY_CHART.NEXT_24_YEARS',
          'ACCESSIBILITY.ENERGY_COCKPIT.ENERGY_CHART.CLOSE_BUTTON',
        ];
        translate.get(translations).subscribe((values) => {
          intl.openCalendarLabel =
            values['ACCESSIBILITY.ENERGY_COCKPIT.ENERGY_CHART.OPEN_CALENDAR'];
          intl.switchToMonthViewLabel =
            values['ACCESSIBILITY.ENERGY_COCKPIT.ENERGY_CHART.CHOOSE_DATE'];
          intl.prevYearLabel =
            values['ACCESSIBILITY.ENERGY_COCKPIT.ENERGY_CHART.PREVIOUS_YEAR'];
          intl.nextYearLabel =
            values['ACCESSIBILITY.ENERGY_COCKPIT.ENERGY_CHART.NEXT_YEAR'];
          intl.prevMonthLabel =
            values['ACCESSIBILITY.ENERGY_COCKPIT.ENERGY_CHART.PREVIOUS_MONTH'];
          intl.nextMonthLabel =
            values['ACCESSIBILITY.ENERGY_COCKPIT.ENERGY_CHART.NEXT_MONTH'];
          intl.prevMultiYearLabel =
            values[
              'ACCESSIBILITY.ENERGY_COCKPIT.ENERGY_CHART.PREVIOUS_24_YEARS'
            ];
          intl.nextMultiYearLabel =
            values['ACCESSIBILITY.ENERGY_COCKPIT.ENERGY_CHART.NEXT_24_YEARS'];
          intl.closeCalendarLabel =
            values['ACCESSIBILITY.ENERGY_COCKPIT.ENERGY_CHART.CLOSE_BUTTON'];
        });
        return intl;
      },
      deps: [TranslateService],
    },
  ],
  standalone: false,
})
export class DatePickerComponent implements OnInit {
  @Input() rangeMode!: DateRangeMode;
  @Input() disabled = true;

  @Output() selectedDate = new EventEmitter<Date>();
  date = new FormControl(new Date());

  minDate: Date;
  maxDate: Date;

  constructor(
    public dateService: DateService,
    private translate: TranslateService,
  ) {
    this.minDate = startOfYear(new Date(2015, 0, 0));
    this.maxDate = endOfDay(new Date());
  }

  ngOnInit() {
    this.date.valueChanges.subscribe((newDate) => {
      if (newDate) {
        this.selectedDate.next(newDate);
      }
    });
  }

  setPrevious() {
    this.jumpDate('prev');
  }
  setNext() {
    this.jumpDate('next');
  }

  canJumpPrevious() {
    return this.canJumpDate('prev');
  }

  canJumpNext() {
    return this.canJumpDate('next');
  }

  jumpDate(direction: 'next' | 'prev') {
    if (!this.date.value) {
      return;
    }

    this.date.setValue(this.getNextDate(this.date.value, direction));
  }

  canJumpDate(direction: 'next' | 'prev') {
    if (!this.date.value) {
      return false;
    }

    const newDate = this.getNextDate(this.date.value, direction);
    return (
      isAfter(endOfDay(newDate), this.minDate) &&
      isBefore(newDate, this.maxDate)
    );
  }

  onMonthSelected(
    normalizedMonthAndYear: Date,
    datepicker: MatDatepicker<Date>,
  ) {
    if (this.rangeMode === DateRangeMode.MONTH) {
      this.date.setValue(normalizedMonthAndYear);
      datepicker.close();
    }
  }

  onYearSelected(normalizedYear: Date, datepicker: MatDatepicker<Date>) {
    if (this.rangeMode === DateRangeMode.YEAR) {
      this.date.setValue(normalizedYear);
      datepicker.close();
    }
  }
  onDatePickerOpened() {
    setTimeout(() => {
      const periodButton = document.querySelector(
        '.mat-calendar-period-button',
      );
      if (periodButton) {
        this.applyPersistentAttributes(<HTMLElement>periodButton);
      }
    });
  }

  private applyPersistentAttributes(element: HTMLElement) {
    element.setAttribute('aria-hidden', 'true');
    element.setAttribute('tabindex', '-1');
  }

  get startView() {
    switch (this.rangeMode) {
      case DateRangeMode.DAY:
        return 'month';
      case DateRangeMode.MONTH:
        return 'year';
      case DateRangeMode.YEAR:
      default:
        return 'multi-year';
    }
  }

  get dateFormat() {
    switch (this.rangeMode) {
      case DateRangeMode.YEAR:
        return 'y';
      case DateRangeMode.MONTH:
        return 'MMMM y';
      case DateRangeMode.DAY:
        return 'dd. MMMM y';
      default:
        return '';
    }
  }

  private getNextDate(date: Date, direction: 'next' | 'prev') {
    const valueToAdd = 1 * (direction === 'next' ? 1 : -1);
    const duration =
      this.rangeMode === DateRangeMode.DAY
        ? { days: valueToAdd }
        : this.rangeMode === DateRangeMode.MONTH
          ? { months: valueToAdd }
          : { years: valueToAdd };

    return dateAdd(date, duration);
  }
}
